import * as PIXI from "pixi.js";
import { hexToNumber } from "../../utils/color";
import type { RhombusProps } from "./types";
import { applyYogaStyle } from "../layoutEngine/applyYogaStyles";
import { coreComponentCommon } from "./coreComponentsCommon";
import { defaultNodeLayoutStyle } from "../layoutEngine/layoutConstants";

export type Rhombus = ReturnType<typeof createRhombus>;

export const createRhombus = (initialProps: RhombusProps) => {
  const pixiComponent: PIXI.Graphics = new PIXI.Graphics();
  pixiComponent.position.set(-Infinity, -Infinity);

  let lastShapeKey: string = "";
  let lastPositionKey: string = "";
  let wasVisible: boolean = true;
  let props: RhombusProps = structuredClone(defaultProps);
  const {
    yogaNode,
    getLayoutValues,
    setLayoutValues,
    getZIndex,
    isCulled,
    setCulled
  } = coreComponentCommon();

  updateProps(initialProps);

  return {
    get props() {
      return props;
    },
    get pixiComponent() {
      return pixiComponent;
    },
    get yogaNode() {
      return yogaNode;
    },
    isCulled,
    setCulled,
    getLayoutValues,
    setLayoutValues,
    getZIndex,
    updateProps,
    destroy,
    render
  };

  /**
   * Performs some basic comparison between new and old props and
   * triggers appropriate actions based on certain props' changes.
   * For Rhombus component, these include:
   * 1. Binding/Unbinding of event handler callbacks
   * 2. Toggling visibility
   * @param newProps
   */
  function updateProps(newProps: RhombusProps) {
    // this._updateEventBindings(newProps);

    props = {
      ...defaultProps,
      ...newProps,
      style: { ...defaultProps.style, ...newProps.style }
    };

    applyYogaStyle({ style: props.style, node: yogaNode });
  }

  function destroy() {
    if (yogaNode.getParent()) {
      // If we don't do this then the parent somehow still
      // considers the node to be taking up space. Which then
      // causes the layout to not be updated in case where
      // you delete a node (Field, for example)
      yogaNode.getParent()?.removeChild(yogaNode);
    }
    yogaNode.free();
    pixiComponent.destroy();
  }

  function renderGraphics() {
    const {
      backgroundColor,
      borderWidth = 0,
      borderColor,
      borderRadius = 0
    } = props.style;

    const { width, height } = getLayoutValues();
    pixiComponent.clear();
    if (backgroundColor || borderWidth) {
      pixiComponent.beginFill(
        hexToNumber(backgroundColor || "#000000"),
        backgroundColor ? 1 : 0
      );

      const radius = borderRadius;
      // we are drawing rhombus at 0, 0 then it will be moved to the correct position
      const x = 0;
      const y = 0;
      const boxMidPoint = { x: x + width / 2, y: y + height / 2 };

      // corner point
      const topPoint = { x: boxMidPoint.x, y: y };
      const rightPoint = { x: x + width, y: boxMidPoint.y };
      const bottomPoint = { x: boxMidPoint.x, y: y + height };
      const leftPoint = { x: x, y: boxMidPoint.y };

      const getMidPointOnLine = (startPoint: number, endPoint: number) => {
        return startPoint + (endPoint - startPoint) / 2;
      };

      // side mid point
      const leftTopMidPoint = {
        x: getMidPointOnLine(x, topPoint.x),
        y: getMidPointOnLine(y, rightPoint.y)
      };
      const rightTopMidPoint = {
        x: getMidPointOnLine(topPoint.x, rightPoint.x),
        y: leftTopMidPoint.y
      };
      const rightBottomMidPoint = {
        x: rightTopMidPoint.x,
        y: getMidPointOnLine(rightPoint.y, bottomPoint.y)
      };
      const leftBottomMidPoint = {
        x: leftTopMidPoint.x,
        y: rightBottomMidPoint.y
      };

      // Draw diamond with rounded corners
      pixiComponent.moveTo(leftTopMidPoint.x, leftTopMidPoint.y);

      // Top to right
      pixiComponent.arcTo(
        topPoint.x,
        topPoint.y,
        rightTopMidPoint.x,
        rightTopMidPoint.y,
        radius
      );

      // Right to bottom
      pixiComponent.arcTo(
        rightPoint.x,
        rightPoint.y,
        rightBottomMidPoint.x,
        rightBottomMidPoint.y,
        radius
      );

      // Bottom to left
      pixiComponent.arcTo(
        bottomPoint.x,
        bottomPoint.y,
        leftBottomMidPoint.x,
        leftBottomMidPoint.y,
        radius
      );

      // Left to top
      pixiComponent.arcTo(
        leftPoint.x,
        leftPoint.y,
        leftTopMidPoint.x,
        leftTopMidPoint.y,
        radius
      );
      pixiComponent.lineTo(leftTopMidPoint.x, leftTopMidPoint.y);

      if (borderWidth && borderColor) {
        pixiComponent.lineStyle(borderWidth, hexToNumber(borderColor));
      }

      pixiComponent.endFill();
    }
  }

  function render(raised: boolean = false) {
    const {
      backgroundColor,
      borderWidth = 0,
      borderColor,
      borderRadius = 0,
      visible: isVisible
    } = props.style;

    const shouldBeVisible: boolean = !!isVisible && !isCulled();

    if (shouldBeVisible !== wasVisible) {
      pixiComponent.visible = shouldBeVisible;
      wasVisible = shouldBeVisible;
    }

    const { width, height } = getLayoutValues();
    const shapeKey = `${width},${height},${backgroundColor},${borderWidth},${borderColor},${borderRadius}`;

    // do we need to re-draw?
    if (lastShapeKey !== shapeKey) {
      renderGraphics();
      lastShapeKey = shapeKey;
    }

    const { left, top } = getLayoutValues();
    const zIndex = getZIndex();
    const positionKey = `${left},${top},${zIndex}`;

    // do we need to reposition?
    if (lastPositionKey !== positionKey) {
      pixiComponent.position.x = left;
      pixiComponent.position.y = top;
      if (raised) {
        pixiComponent.zIndex = 100;
      } else {
        pixiComponent.zIndex = 0;
      }
      lastPositionKey = positionKey;
    }
  }
};

const defaultProps: RhombusProps = {
  key: "",
  style: defaultNodeLayoutStyle
};
